import { StyleSheet, View } from 'react-native';
import { TabView } from 'react-native-tab-view';
import { useSelector } from 'react-redux';
import { Text } from '@ui-kitten/components';

import constants from '../../../Config/constants';
import { selectMealPlanCounter } from '../../../RTK/mealPlan/selectors';
import MealPlanHint, { HintKey } from '../../../Screens/Store/MealPlanHint';
import themeStyle from '../../../Theme/styles';

import Button from '../../Button';
import CartSummary from '../../StoreCart/CartSummary';

import Breadcrumbs from './Breadcrumbs';
import useMealPlanList from './controller/useMealPlanList';
import DayItemList from './DayItemList';
import { RenderProps } from './types';

const MealPlanListRender = ({ isDisplayOnly, scrollHandlers }: RenderProps) => {
  const {
    allCart,
    cartExceedMonday,
    days,
    dispatchTime,
    isBrandedPaymentLinkLoading,
    isDayHasCart,
    isFirstDay,
    isLastDay,
    isMealPlanAndHasType,
    mealPlanDiscount,
    sceneHeight,
    selectedDay,
    selectedDayIndex,
    tabRoutes,
    onBreadCrumbsPress,
    onCreate,
    onNext,
    onPrev,
  } = useMealPlanList();
  const cartCounter = useSelector((state) =>
    selectMealPlanCounter(state, selectedDay?.value),
  );

  if ((!isMealPlanAndHasType && !isDisplayOnly) || tabRoutes.length === 0) {
    return null;
  }

  return (
    <View style={constants.isWeb && styles.webWrapper}>
      {!isDisplayOnly && (
        <View
          style={[
            themeStyle.pageHorizontalSpacing,
            themeStyle.spacingTopMedium,
          ]}
        >
          <Text>Dispatch Time: {dispatchTime}</Text>
        </View>
      )}
      {/* Monday to Friday Breadcrumbs */}
      <Breadcrumbs
        data={days}
        value={selectedDay?.value}
        onClick={onBreadCrumbsPress}
      />
      {/* Meal Plan Items base on selected date (refer to tabRoutes for the selected day) */}
      <View
        style={[
          themeStyle.spacingBottomMedium,
          constants.isWeb ? themeStyle.flex1 : { height: sceneHeight }, // calculated height needed when TabView is used in ActionSheet
        ]}
      >
        <TabView
          swipeEnabled={false}
          renderTabBar={() => null}
          navigationState={{ index: selectedDayIndex, routes: tabRoutes }}
          onIndexChange={() => {}} // do nothing, need to pass because this props is required
          renderScene={(props) => (
            <DayItemList
              {...props}
              isDisplayOnly={isDisplayOnly}
              scrollHandlers={scrollHandlers}
            />
          )}
          animationEnabled
          lazy
        />
      </View>
      {/* Summary */}
      {!isDisplayOnly && isLastDay && isDayHasCart && (
        <CartSummary cart={allCart} subTotalPromoPercent={mealPlanDiscount} />
      )}
      {/* Navigation Buttons */}
      {!isDisplayOnly && (
        <View style={themeStyle.pageHorizontalSpacing}>
          <View
            style={
              !constants.isWeb && themeStyle.flexDirectionRowCenterSpaceBetween
            }
          >
            {/* Hint doesn't trigger on mobile */}
            {!isFirstDay && (
              <View
                style={[
                  themeStyle.flex1,
                  constants.isWeb
                    ? themeStyle.spacingBottomMedium
                    : themeStyle.spacingRightMedium,
                ]}
              >
                <MealPlanHint hintKey={HintKey.PREV}>
                  <Button status="control" style={styles.back} onPress={onPrev}>
                    Back
                  </Button>
                </MealPlanHint>
              </View>
            )}
            {/* Next Hint doesn't trigger on mobile */}
            <View style={themeStyle.flex1}>
              <MealPlanHint hintKey={isLastDay ? HintKey.CREATE : HintKey.NEXT}>
                <Button
                  disabled={cartExceedMonday.isExceed}
                  loading={isBrandedPaymentLinkLoading}
                  status="success"
                  onPress={isLastDay ? onCreate : onNext}
                >
                  {(props) => (
                    <Text {...props} numberOfLines={1} adjustsFontSizeToFit>
                      {isLastDay ? 'Create meal plan' : 'Next'}{' '}
                      {isFirstDay || cartCounter.current === cartCounter.monday
                        ? ''
                        : `(${cartCounter.current}/${cartCounter.monday})`}
                    </Text>
                  )}
                </Button>
              </MealPlanHint>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  webWrapper: {
    ...themeStyle.flex1,
    marginHorizontal: -15, // to offset horizontal spacing of container on web
  },
  back: { backgroundColor: '#edf1f7' },
});

export default MealPlanListRender;
